// components/GoogleMap.jsx
// Wrapper minimalista do Google Maps JS API.
// Carrega o script via injeção dinâmica (uma vez) e expõe ref do mapa para customização.
//
// Uso:
//   <GoogleMap center={[lat,lng]} zoom={14} markers={[{lat,lng,label,color}]} />

import { useEffect, useRef, useState } from 'react';

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_FRONTEND_KEY;
let _scriptPromise = null;

function carregarScript() {
  if (_scriptPromise) return _scriptPromise;
  if (window.google?.maps) return Promise.resolve();
  if (!API_KEY) return Promise.reject(new Error('VITE_GOOGLE_MAPS_FRONTEND_KEY não definida'));

  _scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=geometry&language=pt-BR&region=BR`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Falha ao carregar Google Maps'));
    document.head.appendChild(s);
  });
  return _scriptPromise;
}

// Aguarda window.google.maps.Map estar realmente disponível (até 3s).
// Cobre o caso do loading=async ainda em qualquer cache antigo do browser
// ou carregamento parcial do bootstrap.
function aguardarMapsDisponivel(timeoutMs = 3000) {
  return new Promise((resolve, reject) => {
    if (window.google?.maps?.Map) return resolve();
    const t0 = Date.now();
    const iv = setInterval(() => {
      if (window.google?.maps?.Map) {
        clearInterval(iv);
        resolve();
      } else if (Date.now() - t0 > timeoutMs) {
        clearInterval(iv);
        reject(new Error('Google Maps demorou demais pra carregar'));
      }
    }, 50);
  });
}

/**
 * @param {object} props
 * @param {[number,number]} props.center  - [lat, lng]
 * @param {number} props.zoom
 * @param {number} props.height           - altura em px (default 360)
 * @param {Array<{lat,lng,label?,color?,onClick?,info?}>} props.markers
 * @param {Array<[number,number]>} props.polyline  - lista de [lat,lng]
 * @param {Array<{lat,lng,radius?,color?,info?}>} props.circles
 * @param {function} props.onMapReady  - callback(map, google) — pra customizar mais
 */
export default function GoogleMap({
  center = [-27.595, -48.548],
  zoom = 13,
  height = 360,
  markers = [],
  polyline = [],
  circles = [],
  onMapReady,
}) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const overlaysRef = useRef([]);
  const [erro, setErro] = useState(null);

  // 1) Inicializa o mapa quando script carrega
  // Defesa em profundidade: mesmo após carregarScript().then(), aguarda
  // ativamente window.google.maps.Map estar disponível (até 3s) — protege
  // contra qualquer race do loader async/cache.
  useEffect(() => {
    let cancelado = false;
    carregarScript()
      .then(() => aguardarMapsDisponivel())
      .then(() => {
        if (cancelado || !ref.current) return;
        const map = new window.google.maps.Map(ref.current, {
          center: { lat: center[0], lng: center[1] },
          zoom,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        });
        mapRef.current = map;
        if (onMapReady) onMapReady(map, window.google);
      })
      .catch(e => setErro(e.message));
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2) Atualiza centro/zoom
  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setCenter({ lat: center[0], lng: center[1] });
    mapRef.current.setZoom(zoom);
  }, [center[0], center[1], zoom]);

  // 3) Renderiza markers / polyline / circles
  useEffect(() => {
    if (!mapRef.current || !window.google) return;
    const map = mapRef.current;
    const g = window.google;

    // Limpa overlays anteriores
    overlaysRef.current.forEach(o => o.setMap(null));
    overlaysRef.current = [];

    // Markers
    for (const m of markers) {
      let icon;
      if (m.iconUrl) {
        // Avatar SVG / pin custom (data: URL)
        const size = m.iconSize || 32;
        icon = {
          url: m.iconUrl,
          scaledSize: new g.maps.Size(size, size),
          anchor: new g.maps.Point(size / 2, size / 2),
        };
      } else if (m.color) {
        icon = {
          path: g.maps.SymbolPath.CIRCLE,
          scale: m.scale || 9,
          fillColor: m.color,
          fillOpacity: 0.95,
          strokeColor: '#fff',
          strokeWeight: 2,
        };
      }
      const marker = new g.maps.Marker({
        position: { lat: m.lat, lng: m.lng },
        map,
        title: m.label || '',
        icon,
        zIndex: m.zIndex,
      });
      if (m.info) {
        const info = new g.maps.InfoWindow({ content: m.info });
        marker.addListener('click', () => info.open(map, marker));
      }
      if (m.onClick) marker.addListener('click', m.onClick);
      overlaysRef.current.push(marker);
    }

    // Polyline
    if (polyline.length > 1) {
      const path = polyline.map(([la, ln]) => ({ lat: la, lng: ln }));
      const line = new g.maps.Polyline({
        path,
        geodesic: true,
        strokeColor: '#1e6fc8',
        strokeOpacity: 0.7,
        strokeWeight: 3,
        map,
      });
      overlaysRef.current.push(line);
    }

    // Círculos (paradas detectadas)
    for (const c of circles) {
      const circle = new g.maps.Marker({
        position: { lat: c.lat, lng: c.lng },
        map,
        icon: {
          path: g.maps.SymbolPath.CIRCLE,
          scale: c.radius || 8,
          fillColor: c.color || '#16a34a',
          fillOpacity: 0.7,
          strokeColor: c.color || '#16a34a',
          strokeWeight: 2,
        },
      });
      if (c.info) {
        const info = new g.maps.InfoWindow({ content: c.info });
        circle.addListener('click', () => info.open(map, circle));
      }
      overlaysRef.current.push(circle);
    }
  }, [markers, polyline, circles]);

  if (erro) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', color: '#dc2626', padding: '1rem', textAlign: 'center' }}>
        ⚠ Falha ao carregar Google Maps: {erro}
      </div>
    );
  }

  return <div ref={ref} style={{ width: '100%', height }} />;
}
